import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/membershipType/utils/constants';
import { usePatchMembershipType } from 'src/features/membershipType/api/usePatchMembershipType';
import { AllowedTimes } from 'src/features/membershipType/types';
import { LIST_ALLOWED_TIMES_RESOURCE } from 'src/features/membershipType/api/allowedTime/useListMembershipTypeAllowedTimes';
import { allowedTimesMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface DeleteAllowedTimeProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAllowedTime: AllowedTimes;
    allowedTimesData?: AllowedTimes[];
    children?: React.ReactNode;
}

function DeleteAllowedTime<TData>({ selectedAllowedTime, allowedTimesData, children }: DeleteAllowedTimeProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { membershipTypeId = '' } = useParams();
    const { parkingId } = useParking();

    const { day_of_week, id} = selectedAllowedTime;

    const remainingAllowedTimes = React.useMemo(() => {
        return allowedTimesData?.filter((d) => d.id !== id) ?? [];
    }, [allowedTimesData, id]);

    const { mutate: deleteAllowedTime } = usePatchMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ALLOWED_TIMES_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(allowedTimesMessages.deleteAllowedTimeSuccess),
                description: formatMessage(allowedTimesMessages.deleteAllowedTimeSuccessDescription, {
                    entry: day_of_week && formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[day_of_week]),
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...allowedTimesMessages.deleteAllowedTime },
                subTitle: {
                    ...allowedTimesMessages.deleteAllowedTimeSubtitle,
                    values: {
                        entry: (day_of_week && formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[day_of_week])) ?? '',
                    },
                },
                icon: ICON_MAP.DELETE as IconProp,
                confirmationMessage: {
                    ...allowedTimesMessages.confirmDeleteAllowedTime,
                    values: {
                        entry: (day_of_week && formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[day_of_week])) ?? '',
                    },
                },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => {
                    if (allowedTimesData) {
                        return deleteAllowedTime({
                            parkingId,
                            id: membershipTypeId,
                            membership_type_allowed_times: remainingAllowedTimes,
                        });
                    }
                },
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeleteAllowedTime;
